import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { delay, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';

import { UpsellingApiService } from '@core/services/api/upselling/upselling-api.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { Store } from '@ngrx/store';
import { DoodApiError } from '@shared/interfaces/error.interface';
import { UpsellingModalModalConfig } from '@shared/modals/upselling-alert-modal/upselling-modal.component';
import { statedDataToAction } from '@shared/utils/rxjs/stated-data-to-action';
import { statedStream } from '@shared/utils/rxjs/stated-steam';
import { SELECT_CART_ACTIVE_TYPE } from '@store/cart';
import { SELECT_MARKETPLACE_ITEM } from '@store/marketplace';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product/product.action';
import { SELECT_ORDER_TYPE } from '@store/settings';
import { SELECT_SHOP_ACTIVE_ID } from '@store/shop';
import {
  UPSELLING_STORE_API_ACTIONS,
  UPSELLING_STORE_COMPONENT_ACTIONS,
} from '@store/upselling/upselling.action';
import { combineLatest } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UpsellingEffects {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly upsellingApiService = inject(UpsellingApiService);
  private readonly modalsService = inject<ModalsService<UpsellingModalModalConfig>>(ModalsService);

  _showUpsellingProposalsEffect = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(UPSELLING_STORE_COMPONENT_ACTIONS.showUpsellingProposals),
        tap(({ triggerProductId }) => {
          this.modalsService.open('upselling-modal', undefined, {
            triggerProductId,
          });
        }),
      );
    },
    { dispatch: false },
  );

  _upsellingModalSelectedProposalEffect = createEffect(() => {
    return this.modalsService.event$('upselling-modal').pipe(
      filter(event => event?.type === 'selectedProposal'),
      delay(0),
      map(event => {
        const { selectedProposal, triggerProductId, upsellingId } = event;

        return PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
          productId: selectedProposal.id,
          productShopId: selectedProposal.shop_id ?? '',
          productOrigin: {
            channel: 'up-sell',
            reference: `up_sell_rule_${upsellingId}`,
            trigger_product_id: triggerProductId,
          },
        });
      }),
    );
  });

  _loadActiveShopUpsellingEffect = createEffect(() => {
    return combineLatest({
      activeShopId: this.store.select(SELECT_SHOP_ACTIVE_ID),
      orderType: combineLatest([
        this.store.select(SELECT_ORDER_TYPE),
        this.store.select(SELECT_CART_ACTIVE_TYPE),
      ]).pipe(map(([orderType, cartDistributionMode]) => orderType || cartDistributionMode)),
      marketplaceId: this.store
        .select(SELECT_MARKETPLACE_ITEM)
        .pipe(map(marketplace => marketplace?.id ?? '')),
    }).pipe(
      filter(
        (value): value is { activeShopId: string; orderType: string; marketplaceId: string } =>
          !!value.activeShopId && !!value.orderType,
      ),
      distinctUntilChanged(
        (prev, curr) =>
          prev.activeShopId === curr.activeShopId &&
          prev.orderType === curr.orderType &&
          prev.marketplaceId === curr.marketplaceId,
      ),
      switchMap(({ activeShopId, orderType, marketplaceId }) => {
        // Dispatch the load action when shop becomes active
        return statedDataToAction(
          statedStream(
            this.upsellingApiService.getUpsellingProposals$({
              shopId: activeShopId,
              orderType,
              marketplaceId,
            }),
          ),
          {
            loading: () => UPSELLING_STORE_API_ACTIONS.loadUpselling(),
            failure: (error: DoodApiError) =>
              UPSELLING_STORE_API_ACTIONS.loadUpsellingFailure({ error }),
            success: data => UPSELLING_STORE_API_ACTIONS.loadUpsellingSuccess({ data }),
          },
        );
      }),
    );
  });
}
